'use client';

import './donate-card.scss';

import { cn } from '@sola/ark-core/utils/helpers.ts';
import { FlexibleContentProps, Templates } from '@sola/schema/lib/schema.ts';
import { TrackViewFund } from '@sola/tracking/components/datalayer.tsx';

import { Image } from '../../../atoms/image/image.tsx';
import {
  FeatureBase,
  FeatureBaseProps,
} from '../../../molecules/feature-base/feature-base.tsx';
import { FeatureButtons } from '../../../molecules/feature-buttons/feature-buttons.tsx';
import { getBaseSizeProps } from '../../../utils/text-utils.tsx';
import { DonateProvider } from './donate-context.tsx';
import { DonateForm } from './donate-form.tsx';
import { DonateVariants } from './donate.types.ts';

// @TODO: This card should be a molecule when the donate logic moves to a library at somepoint

type DonateCardProps = FlexibleContentProps & {
  displayLevel?: number;
  variant?: DonateVariants;
  focusOnLoad?: boolean;
  className?: string;
  alt?: boolean;
};

export const DonateCard: React.FC<DonateCardProps> = ({
  seoLevel = 2,
  displayLevel,
  media,
  mediaLogo,
  primaryCTA,
  secondaryCTA,
  tertiaryCTA,
  variant = DonateVariants.default,
  focusOnLoad = false,
  className,
  alt,
  ...props
}) => {
  const featureBaseProps = FeatureBaseProps.parse(props);

  const hasHeaderContent = props.title || props.body;
  const hasButtons = !!primaryCTA || !!secondaryCTA || !!tertiaryCTA;

  // Don't track interstitials.
  const shouldTrackView =
    props.mergeFieldData?.pageData?.template === Templates.enum.giftCatalog ||
    props.mergeFieldData?.pageData?.template === Templates.enum.donate;

  return (
    <div className={cn('DonateCard card-theme', className)}>
      {shouldTrackView && <TrackViewFund fund={props.fund} />}
      {media && (
        <div className="DonateCard__media">
          <Image
            image={media.image}
            aspectRatio="16-9"
            priority={seoLevel === 1}
          />
        </div>
      )}
      <div className="DonateCard__content p-comfy-lg @md:p-roomy-xs flex flex-col gap-comfy-lg">
        {hasHeaderContent && (
          <FeatureBase
            {...featureBaseProps}
            {...getBaseSizeProps(displayLevel || seoLevel)}
            seoLevel={seoLevel}
            media={mediaLogo}
          />
        )}
        {props.fund && (
          <DonateProvider
            {...props}
            seoLevel={hasHeaderContent ? seoLevel + 1 : seoLevel}
            variant={variant}
            alt={alt}
          >
            <DonateForm focusOnLoad={focusOnLoad} alt={alt} />
          </DonateProvider>
        )}
      </div>
      {variant === DonateVariants.grid && hasButtons && (
        <div className="DonateCard__actions p-comfy-lg">
          <FeatureButtons
            {...{ primaryCTA, secondaryCTA, tertiaryCTA }}
            alignment="center"
            isLinks
          />
        </div>
      )}
    </div>
  );
};
